// src/pages/Jeux.js

import { useState } from 'react';
import { getCards, shuffle } from '../data/cardsApi';
import { Card, IntensityPicker } from '../components/Card';
import { Button } from '../components/Button';
import { Modal, ModalTitle } from '../components/Modal';
import { PageShell, PageTitle, Divider, SectionLabel } from '../components/PageShell';
import { C, GRADIENTS, FONTS, RADIUS, INTENSITY } from '../styles/theme';

// ─── Remplacement des tokens {A} {B} {C} {D}… ────────────────
// Chaque token correspond à un rôle distinct dans la carte.
// On passe un tableau de noms dans l'ordre [A, B, C, D…]
function fillMulti(text, names = []) {
  const tokens = ['{A}', '{B}', '{C}', '{D}'];
  let result = text || '';
  tokens.forEach((token, i) => {
    result = result.split(token).join(names[i] || token);
  });
  return result;
}

// Détecte tous les tokens présents dans un texte de carte
function detectTokens(text = '') {
  const tokens = ['{A}', '{B}', '{C}', '{D}'];
  return tokens.filter(t => text.includes(t));
}

// ── Couleurs de fond selon l'intensité (progressivement plus chaude/sombre) ──
const PLAY_BG = {
  1: '#FAF7F2',  // crème — doux
  2: '#F5EBE0',  // pêche pâle — pimenté
  3: '#EDD5C8',  // terracotta pâle — intense
};

// Seuil de points pour monter automatiquement d'un niveau
const SCORE_PER_LEVEL = 5;

function totalScore(scores) {
  return Object.values(scores || {}).reduce((a, b) => a + b, 0);
}
function levelFromScore(scores) {
  return Math.min(3, 1 + Math.floor(totalScore(scores) / SCORE_PER_LEVEL));
}

export function Jeux({ favs, onFav }) {
  const [view,      setView]      = useState('menu');
  const [intensity, setIntensity] = useState(1);
  const [players,   setPlayers]   = useState([
    { name: 'Sofia', f: true,  m: false },
    { name: 'Max',   f: true,  m: true  },
  ]);
  const [newName,  setNewName]   = useState('');
  const [couples,  setCouples]   = useState([]);
  const [exclusif, setExclusif]  = useState(false);
  const [modal,    setModal]     = useState(null);
  const [game,     setGame]      = useState(null);

  // ── Joueurs ──────────────────────────────────────────────────
  function addPlayer() {
    if (!newName.trim()) return;
    setPlayers(p => [...p, { name: newName.trim(), f: false, m: false }]);
    setNewName('');
  }
  function removePlayer(i) { if (players.length > 2) setPlayers(p => p.filter((_, j) => j !== i)); }
  function updatePlayer(i, key, val) { setPlayers(p => p.map((x, j) => j === i ? { ...x, [key]: val } : x)); }

  // ── Démarrage ────────────────────────────────────────────────
  function openExclModal() { setModal('excl'); }
  function confirmExcl(val) {
    setExclusif(val);
    if (val) setModal('couples');
    else launchGame();
  }
  function launchGame() {
    setModal(null);
    const scores = {};
    players.forEach(p => (scores[p.name] = 0));
    setGame({ scores, cur: 0, card: null, usedIds: [], autoIntensity: intensity });
    setView('play');
  }

  // ── Intensité effective ──────────────────────────────────────
  function effectiveIntensity(g) {
    return Math.min(3, Math.max(intensity, g?.autoIntensity || 1));
  }
  function boostIntensity() {
    setGame(g => {
      const cur = effectiveIntensity(g);
      if (cur >= 3) return g;
      return { ...g, autoIntensity: cur + 1 };
    });
  }

  // ── Gestion couples ──────────────────────────────────────────
  function areCouple(a, b) {
    return couples.some(c => (c.a === a && c.b === b) || (c.a === b && c.b === a));
  }
  function isInCouple(idx) {
    return couples.some(c => c.a === idx || c.b === idx);
  }
  function getTargets(playerIdx) {
    if (!exclusif || couples.length === 0) return players.filter((_, i) => i !== playerIdx);
    const pair = couples.find(c => c.a === playerIdx || c.b === playerIdx);
    if (!pair) return players.filter((_, i) => i !== playerIdx);
    const partnerIdx = pair.a === playerIdx ? pair.b : pair.a;
    return [players[partnerIdx]];
  }
  function maxAllowedIntensity(fromIdx, toIdxs, effInt) {
    if (!exclusif || couples.length === 0) return effInt;
    for (const toIdx of toIdxs) {
      if (areCouple(fromIdx, toIdx)) continue;
      if (isInCouple(fromIdx) || isInCouple(toIdx)) return 1;
    }
    return effInt;
  }

  // ── Pioche d'une carte ───────────────────────────────────────
  function pickCardType(type) {
    const effInt  = effectiveIntensity(game);
    const usedIds = game.usedIds || [];
    const others  = players.filter((_, i) => i !== game.cur);

    // Pool complet de cartes disponibles (type + intensité)
    const pool = getCards({ type, context: 'maison', maxIntensity: effInt });
    // Cartes non déjà vues (on réinitialise si toutes épuisées)
    let unseen = pool.filter(c => !usedIds.includes(c.id));
    if (unseen.length === 0) {
      unseen = pool;
      setGame(g => ({ ...g, usedIds: [] }));
    }
    const shuffled = shuffle(unseen);

    // Cherche la première carte dont on peut satisfaire tous les tokens avec les joueurs dispo
    let chosen = null;
    let assignedNames = [];

    for (const candidate of shuffled) {
      const tokens = detectTokens(candidate.text);
      // {A} = joueur actif, les autres tokens = cibles à attribuer
      const targetTokens = tokens.filter(t => t !== '{A}');
      const nbTargets = targetTokens.length;

      // Si la carte n'a pas de tokens (carte générale) → OK directement
      if (nbTargets === 0) {
        chosen = candidate;
        assignedNames = [players[game.cur].name];
        break;
      }

      // Vérifie qu'on a assez de joueurs
      if (nbTargets > others.length) continue;

      // Détermine le nombre réel de cibles à impliquer
      let nb;
      if (others.length === 1) { nb = 1; }
      else {
        const r = Math.random();
        nb = r < 0.60 ? 1 : r < 0.90 ? Math.min(2, others.length) : others.length;
      }
      nb = Math.min(nb, nbTargets); // ne peut pas dépasser le nb de tokens disponibles

      // Sélectionne aléatoirement les cibles
      const pickedTargets = shuffle(others).slice(0, nb);
      const toIdxs = pickedTargets.map(t => players.indexOf(t));

      // Vérifie les restrictions de couples
      const allowed = maxAllowedIntensity(game.cur, toIdxs, effInt);
      if (candidate.intensity > allowed) continue;

      // Tout bon — on construit les noms
      // Si la carte a plus de tokens que de cibles choisies, on complète avec d'autres joueurs
      const allTargetNames = shuffle(others).map(p => p.name);
      const finalNames = [
        players[game.cur].name,         // {A}
        ...allTargetNames.slice(0, targetTokens.length), // {B}, {C}…
      ];

      chosen = candidate;
      assignedNames = finalNames;
      break;
    }

    if (!chosen) return; // Pas de carte disponible (rare)

    const filledText = fillMulti(chosen.text, assignedNames);

    setGame(g => ({
      ...g,
      card: {
        ...chosen,
        text: filledText,
        playerA: assignedNames[0],
        playerB: assignedNames.slice(1).join(', '),
        allPlayers: assignedNames,
      },
      usedIds: [...(g.usedIds || []), chosen.id],
    }));
  }

  function nextTurn(done) {
    setGame(g => {
      const newScores = done
        ? { ...g.scores, [players[g.cur].name]: (g.scores[players[g.cur].name] || 0) + 1 }
        : g.scores;
      const newAutoInt = Math.max(g.autoIntensity || 1, levelFromScore(newScores));
      return { ...g, scores: newScores, cur: (g.cur + 1) % players.length, card: null, autoIntensity: newAutoInt };
    });
  }

  // ── Mode distance ────────────────────────────────────────────
  function pickDistCard(type) {
    const pool = getCards({ type, context: 'distance', maxIntensity: intensity });
    const card = shuffle(pool)[0] || null;
    setGame(g => ({ ...(g || { scores:{}, cur:0, card:null, usedIds:[], autoIntensity:1 }), distCard: card }));
  }

  // ════════════════════════════════════════════════════════════
  // VUES
  // ════════════════════════════════════════════════════════════

  // ── Menu ─────────────────────────────────────────────────────
  if (view === 'menu') return (
    <PageShell>
      <PageTitle>Choisir un jeu</PageTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {MODES.map(m => (
          <button key={m.id} onClick={() => setView(m.id === 'sexting' ? 'dist' : 'setup')} style={{
            padding: '32px 24px', borderRadius: 24, border: 'none',
            background: m.bg, textAlign: 'left', cursor: 'pointer',
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontFamily: FONTS.display, fontSize: 22, color: '#fff', fontStyle: 'italic' }}>{m.label}</div>
            {m.sub && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>{m.sub}</div>}
          </button>
        ))}
      </div>
    </PageShell>
  );

  // ── Setup ─────────────────────────────────────────────────────
  if (view === 'setup') return (
    <PageShell>
      <button onClick={() => setView('menu')} style={{ background: 'none', border: 'none', color: C.textMid, fontSize: 14, fontWeight: '500', marginBottom: 28, cursor: 'pointer' }}>
        ← Retour
      </button>

      <SectionLabel>Niveau de départ</SectionLabel>
      <IntensityPicker value={intensity} onChange={setIntensity} />
      <p style={{ fontSize: 12, color: C.textLight, marginTop: 8, fontStyle: 'italic' }}>
        L'intensité montera automatiquement tous les {SCORE_PER_LEVEL} points.
      </p>
      <Divider style={{ margin: '24px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
        <span style={{ fontFamily: FONTS.display, fontSize: 20, color: C.text, fontStyle: 'italic' }}>Joueur·se·s</span>
        <span style={{ fontSize: 12, color: C.textLight }}>intérêts →</span>
      </div>

      {players.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <input value={p.name} onChange={e => updatePlayer(i, 'name', e.target.value)} style={{
            flex: 1, background: 'none', border: 'none',
            borderBottom: `1.5px solid ${C.sand}`,
            color: C.text, fontSize: 17, fontWeight: '500', padding: '4px 0',
          }} />
          <button onClick={() => updatePlayer(i, 'f', !p.f)} style={{ background: 'none', border: 'none', fontSize: 20, opacity: p.f ? 1 : 0.25, cursor: 'pointer' }}>👩</button>
          <button onClick={() => updatePlayer(i, 'm', !p.m)} style={{ background: 'none', border: 'none', fontSize: 20, opacity: p.m ? 1 : 0.25, cursor: 'pointer' }}>👨</button>
          {players.length > 2 && <button onClick={() => removePlayer(i)} style={{ background: 'none', border: 'none', fontSize: 15, color: C.textLight, cursor: 'pointer' }}>✕</button>}
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 36 }}>
        <span onClick={addPlayer} style={{ fontSize: 22, cursor: 'pointer', color: C.lilasDark }}>＋</span>
        <input value={newName} onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addPlayer()}
          placeholder="Ajouter un joueur…"
          style={{ flex: 1, background: 'none', border: 'none', borderBottom: `1.5px solid ${C.sand}`, color: C.text, fontSize: 15, padding: '4px 0' }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Button onClick={openExclModal} full size="lg">Jouer ✦</Button>
        <Button onClick={openExclModal} variant="muted" full>Jouer sans les noms</Button>
      </div>

      {modal === 'excl' && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Jouer en couple ?</ModalTitle>
          <p style={{ fontSize: 14, color: C.textMid, marginBottom: 28, lineHeight: 1.6 }}>
            S'il y a des couples, les gages seront dirigés entre partenaires et l'intensité plafonnée avec les autres.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button onClick={() => confirmExcl(true)} full>Oui 💕</Button>
            <Button onClick={() => confirmExcl(false)} variant="muted" full>Non 😏</Button>
          </div>
        </Modal>
      )}
      {modal === 'couples' && (
        <Modal onClose={() => setModal(null)}>
          <ModalTitle>Qui est en couple ?</ModalTitle>
          <p style={{ fontSize: 13, color: C.textLight, marginBottom: 20, lineHeight: 1.5 }}>
            Sélectionne deux noms pour former une paire.
          </p>
          <CouplesPicker players={players} couples={couples} setCouples={setCouples} />
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button onClick={launchGame} full>Commencer ✦</Button>
            <Button onClick={launchGame} variant="ghost">Passer</Button>
          </div>
        </Modal>
      )}
    </PageShell>
  );

  // ── Gameplay ──────────────────────────────────────────────────
  if (view === 'play' && game) {
    const name    = players[game.cur]?.name || '?';
    const effInt  = effectiveIntensity(game);
    const intInfo = INTENSITY[effInt];
    const bgColor = PLAY_BG[effInt];
    const total   = totalScore(game.scores);
    const progress = ((total % SCORE_PER_LEVEL) / SCORE_PER_LEVEL) * 100;

    return (
      <div style={{ background: bgColor, minHeight: '100vh', padding: '52px 20px 90px', transition: 'background 1.2s ease' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <button onClick={() => setView('menu')} style={{ background: 'none', border: 'none', color: C.textMid, fontSize: 14, fontWeight: '500', cursor: 'pointer' }}>← Jeux</button>
          <button onClick={() => setView('end')} style={{ background: 'rgba(61,46,36,0.08)', border: 'none', borderRadius: RADIUS.pill, padding: '8px 14px', fontSize: 13, color: C.textMid, cursor: 'pointer' }}>
            Terminer
          </button>
        </div>

        {/* Barre d'intensité */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 20 }}>{intInfo.icon}</span>
              <span style={{ fontSize: 13, fontWeight: '600', color: C.textMid }}>{intInfo.label}</span>
              {effInt < 3
                ? <span style={{ fontSize: 11, color: C.textLight }}>· {SCORE_PER_LEVEL - (total % SCORE_PER_LEVEL)} pts → {INTENSITY[effInt + 1]?.icon}</span>
                : <span style={{ fontSize: 11, color: C.textLight }}>· Niveau max 🔥</span>}
            </div>
            {effInt < 3 && (
              <button onClick={boostIntensity} style={{
                background: 'none', border: `1px solid ${C.sand}`, borderRadius: RADIUS.pill,
                padding: '5px 12px', fontSize: 12, color: C.textMid, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                Monter {INTENSITY[effInt + 1]?.icon}
              </button>
            )}
          </div>
          {effInt < 3 && (
            <div style={{ height: 4, background: C.sand, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 4, background: intInfo.color, width: `${progress}%`, transition: 'width 0.5s ease' }} />
            </div>
          )}
        </div>

        {/* Scores */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, overflowX: 'auto', paddingBottom: 4 }}>
          {players.map(p => (
            <div key={p.name} style={{
              background: p.name === name ? C.rose : 'rgba(255,255,255,0.55)',
              borderRadius: 16, padding: '8px 14px', flexShrink: 0, transition: 'background 0.3s',
            }}>
              <div style={{ fontSize: 10, color: p.name === name ? 'rgba(255,255,255,0.8)' : C.textLight, fontWeight: '600' }}>{p.name}</div>
              <div style={{ fontSize: 18, fontWeight: '600', color: p.name === name ? C.white : C.text, textAlign: 'center' }}>{game.scores[p.name] || 0}</div>
            </div>
          ))}
        </div>

        {/* Choix Action / Vérité */}
        {!game.card ? (
          <>
            <div style={{ fontFamily: FONTS.display, fontSize: 24, color: C.text, fontStyle: 'italic', marginBottom: 28, textAlign: 'center' }}>
              Au tour de {name} ✦
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <button onClick={() => pickCardType('action')} style={{
                padding: '48px 24px', borderRadius: 24, border: 'none',
                background: GRADIENTS.primary, fontFamily: FONTS.display,
                fontSize: 32, color: '#fff', fontStyle: 'italic', cursor: 'pointer',
                boxShadow: '0 6px 28px rgba(240,196,168,0.4)',
              }}>Action</button>
              <button onClick={() => pickCardType('verite')} style={{
                padding: '48px 24px', borderRadius: 24, border: 'none',
                background: GRADIENTS.secondary, fontFamily: FONTS.display,
                fontSize: 32, color: '#fff', fontStyle: 'italic', cursor: 'pointer',
                boxShadow: '0 6px 28px rgba(196,181,232,0.4)',
              }}>Vérité</button>
            </div>
          </>
        ) : (
          <div className="anim-fade-in">
            {/* Joueurs impliqués */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              {(game.card.allPlayers || [game.card.playerA]).map((name, i) => (
                <span key={i} style={{
                  background: i === 0 ? C.rose : C.lilas,
                  color: i === 0 ? '#fff' : C.text,
                  borderRadius: 50, padding: '4px 14px', fontSize: 13, fontWeight: '600',
                }}>{name}</span>
              ))}
            </div>
            {/* Carte */}
            <div style={{
              background: game.card.type === 'action' ? GRADIENTS.primary : GRADIENTS.secondary,
              borderRadius: 24, padding: '36px 28px', marginBottom: 24, textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, fontWeight: '600', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>
                {game.card.type === 'action' ? 'Action' : 'Vérité'}
              </div>
              <div style={{ fontFamily: FONTS.display, fontSize: 20, color: '#fff', lineHeight: 1.5, fontStyle: 'italic' }}>
                {game.card.text}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button onClick={() => nextTurn(false)} variant="muted" full>Passer</Button>
              <Button onClick={() => nextTurn(true)} full>Fait ✓</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Écran de fin — classement ─────────────────────────────────
  if (view === 'end' && game) {
    const ranking = [...players]
      .map(p => ({ name: p.name, score: game.scores[p.name] || 0 }))
      .sort((a, b) => b.score - a.score);
    const effInt = effectiveIntensity(game);
    const medals = ['🥇', '🥈', '🥉'];

    return (
      <PageShell>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>{INTENSITY[effInt].icon}</div>
          <div style={{ fontFamily: FONTS.display, fontSize: 30, color: C.text, fontStyle: 'italic' }}>Classement final</div>
          <div style={{ fontSize: 13, color: C.textLight, marginTop: 6 }}>
            {totalScore(game.scores)} actions accomplies · Niveau {INTENSITY[effInt].label}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 36 }}>
          {ranking.map((p, i) => (
            <div key={p.name} className="anim-fade-in" style={{
              animationDelay: `${i * 0.1}s`,
              display: 'flex', alignItems: 'center', gap: 16,
              background: i === 0 ? GRADIENTS.primary : 'rgba(255,255,255,0.6)',
              borderRadius: 20, padding: '16px 20px',
            }}>
              <span style={{ fontSize: 28, flexShrink: 0 }}>{medals[i] || '✦'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.display, fontSize: 20, color: i === 0 ? '#fff' : C.text, fontStyle: 'italic' }}>{p.name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 26, fontWeight: '700', color: i === 0 ? '#fff' : C.text }}>{p.score}</div>
                <div style={{ fontSize: 11, color: i === 0 ? 'rgba(255,255,255,0.7)' : C.textLight }}>pt{p.score > 1 ? 's' : ''}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button onClick={launchGame} full size="lg">Rejouer ✦</Button>
          <Button onClick={() => setView('menu')} variant="muted" full>Changer de jeu</Button>
        </div>
      </PageShell>
    );
  }

  // ── Mode distance ─────────────────────────────────────────────
  if (view === 'dist') return (
    <PageShell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <button onClick={() => setView('menu')} style={{ background: 'none', border: 'none', color: C.textMid, fontSize: 14, fontWeight: '500', cursor: 'pointer' }}>← Jeux</button>
        <span style={{ background: GRADIENTS.dist, borderRadius: 16, padding: '6px 14px', fontSize: 12, color: '#fff', fontWeight: '600' }}>✦ À distance</span>
      </div>
      <div style={{ fontFamily: FONTS.display, fontSize: 26, color: C.text, fontStyle: 'italic', marginBottom: 6 }}>Action Vérité</div>
      <p style={{ fontSize: 14, color: C.textMid, marginBottom: 20 }}>Mode sexting — juste vous deux 💌</p>
      <SectionLabel>Intensité</SectionLabel>
      <IntensityPicker value={intensity} onChange={setIntensity} />
      <div style={{ height: 24 }} />
      {!game?.distCard ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <button onClick={() => pickDistCard('action')} style={{
            padding: '48px 24px', borderRadius: 24, border: 'none',
            background: GRADIENTS.warm, fontFamily: FONTS.display,
            fontSize: 28, color: C.text, fontStyle: 'italic', cursor: 'pointer',
          }}>Action</button>
          <button onClick={() => pickDistCard('verite')} style={{
            padding: '48px 24px', borderRadius: 24, border: 'none',
            background: `linear-gradient(135deg, ${C.cardVerite}, ${C.lilas})`,
            fontFamily: FONTS.display, fontSize: 28, color: C.text, fontStyle: 'italic', cursor: 'pointer',
          }}>Vérité</button>
        </div>
      ) : (
        <div className="anim-fade-in">
          <Card card={game.distCard} isFav={favs.some(f => f.id === game.distCard.id)} onFav={onFav} />
          <div style={{ marginTop: 16 }}>
            <Button onClick={() => setGame(g => ({ ...g, distCard: null }))} variant="muted" full>Nouvelle carte</Button>
          </div>
        </div>
      )}
    </PageShell>
  );

  return null;
}

// ── Composant configuration des couples ──────────────────────
function CouplesPicker({ players, couples, setCouples }) {
  const [selA, setSelA] = useState(null);
  function selectPlayer(idx) {
    if (selA === null) { setSelA(idx); return; }
    if (selA === idx)  { setSelA(null); return; }
    const exists = couples.find(c => (c.a === selA && c.b === idx) || (c.a === idx && c.b === selA));
    if (exists) setCouples(cs => cs.filter(c => !((c.a === selA && c.b === idx) || (c.a === idx && c.b === selA))));
    else        setCouples(cs => [...cs, { a: selA, b: idx }]);
    setSelA(null);
  }
  function partnerName(i) {
    const c = couples.find(c => c.a === i || c.b === i);
    return c ? players[c.a === i ? c.b : c.a]?.name : null;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {players.map((p, i) => {
        const partner = partnerName(i);
        const isSelA  = selA === i;
        return (
          <button key={i} onClick={() => selectPlayer(i)} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderRadius: 14, border: 'none', cursor: 'pointer',
            background: isSelA ? C.lilas : partner ? '#EEF5EE' : C.bgMuted,
            transition: 'background 0.2s',
          }}>
            <span style={{ fontWeight: '600', color: C.text }}>{p.name}</span>
            {partner && <span style={{ fontSize: 12, color: C.sageDark, fontWeight: '500' }}>💕 {partner}</span>}
            {isSelA  && <span style={{ fontSize: 12, color: C.lilasDark }}>sélectionné·e</span>}
          </button>
        );
      })}
    </div>
  );
}

const MODES = [
  { id: 'maison',    label: 'Action Vérité',   sub: 'en présence',          bg: GRADIENTS.primary },
  { id: 'sexting',   label: 'Action Vérité',   sub: 'à distance · sexting', bg: GRADIENTS.dist },
  { id: 'adventure', label: 'Adventure House', sub: '',                      bg: GRADIENTS.teal },
];
